// ** React Imports
import { useEffect, useCallback, useState, ReactElement, useRef } from 'react'

// ** Next Images
import { Link } from 'react-router-dom'

// ** MUI Imports
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import { DataGrid } from '@mui/x-data-grid'
import { styled } from '@mui/material/styles'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { SelectChangeEvent } from '@mui/material/Select'

// ** Icons Imports
import Laptop from 'mdi-material-ui/Laptop'
import ChartDonut from 'mdi-material-ui/ChartDonut'
import CogOutline from 'mdi-material-ui/CogOutline'
import EyeOutline from 'mdi-material-ui/EyeOutline'
import PencilOutline from 'mdi-material-ui/PencilOutline'
import AccountOutline from 'mdi-material-ui/AccountOutline'

// ** Store Imports


// ** Custom Components Imports
import CustomChip from 'src/materio/@core/components/mui/chip'
import CustomAvatar from 'src/materio/@core/components/mui/avatar'

// ** Utils Import
import { getInitials } from 'src/materio/@core/utils/get-initials'

// ** Actions Imports


// ** Types Imports

import { ThemeColor } from 'src/materio/@core/layouts/types'

// ** Custom Components Imports
import TableHeader from 'src/features/roles/TableHeader'
import { client } from '../../utils/axios-client'
import { getUsersEndpoint, roleUsersEndpoint, toggleUserInRoleEndpoint } from '../../configs/endpoints'
import { PagedList, UserOverview } from '../../types/common'
import _ from 'lodash'
import { Checkbox } from '@mui/material'

interface UserRoleType {
    [key: string]: ReactElement
}

interface UserStatusType {
    [key: string]: ThemeColor
}

interface CellType {
    row: UserOverview
}

// ** Vars
const userRoleObj: UserRoleType = {
    admin: <Laptop fontSize='small' sx={{ mr: 3, color: 'error.main' }} />,
    author: <CogOutline fontSize='small' sx={{ mr: 3, color: 'warning.main' }} />,
    editor: <PencilOutline fontSize='small' sx={{ mr: 3, color: 'info.main' }} />,
    maintainer: <ChartDonut fontSize='small' sx={{ mr: 3, color: 'success.main' }} />,
    subscriber: <AccountOutline fontSize='small' sx={{ mr: 3, color: 'primary.main' }} />
}

const userStatusObj: UserStatusType = {
    active: 'success',
    pending: 'warning',
    inactive: 'secondary'
}

// ** Styled component for the link for the avatar with image
const AvatarWithImageLink = styled(Link)(({ theme }) => ({
    marginRight: theme.spacing(3)
}))

// ** Styled component for the link for the avatar without image
const AvatarWithoutImageLink = styled(Link)(({ theme }) => ({
    textDecoration: 'none',
    marginRight: theme.spacing(3)
}))

// ** renders client column
const renderClient = (row: UserOverview) => {
    if (row.avatar && row.avatar.length) {
        return (
            <AvatarWithImageLink to={`/apps/user/view/${row.id}`}>
                <CustomAvatar src={row.avatar} sx={{ mr: 3, width: 30, height: 30 }} />
            </AvatarWithImageLink>
        )
    } else {
        return (
            <AvatarWithoutImageLink to={`/apps/user/view/${row.id}`}>
                <CustomAvatar skin='light' sx={{ mr: 3, width: 30, height: 30, fontSize: '.875rem' }}>
                    {getInitials(row.fullName ? row.fullName : 'John Doe')}
                </CustomAvatar>
            </AvatarWithoutImageLink>
        )
    }
}

type Props = {
    selectedRole: {
        id: string
    }
}

const UserList = ({ selectedRole }: Props) => {
    // ** State
    const userTableRef = useRef<HTMLDivElement | null>(null);
    const [users, setUsers] = useState([] as UserOverview[])
    const [plan, setPlan] = useState<string>('')
    const [value, setValue] = useState<string>('')
    const [pageSize, setPageSize] = useState<number>(10)
    const [allUsers, setAllUsers] = useState([] as UserOverview[])
    const [filteredUsers, setFilteredUsers] = useState([] as UserOverview[])
    // ** Hooks

    useEffect(() => {
        const loadAllUsers = async () => {            
            const response = await client.get<UserOverview[]>(getUsersEndpoint, { params: { pageNumber: 1, pageSize: 10000 } });  
            setAllUsers(response.data);
        }

        loadAllUsers();
    },[selectedRole])

    useEffect(() => {
        loadRoleUsers();
    }, [selectedRole])

    useEffect(() => {
        if (!userTableRef.current) return;

        userTableRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'end'
        });
    },[allUsers])

    useEffect(() => {        
        if (allUsers) {
            setFilteredUsers(allUsers.filter(u => value == '' || (u.email.toUpperCase().includes(value.toUpperCase()) || u.fullName.toUpperCase().includes(value.toUpperCase()))));
        }
    },[value, users])

    const loadRoleUsers = async () => {
        if (!selectedRole.id || _.isEmpty(selectedRole.id)) {
            setUsers([]);
            return;
        }

        const response = await client.get(roleUsersEndpoint.replace("{id}", selectedRole.id));        
        setUsers(response.data);
    }

    const isUserInRole = (userId: string) => {
        return users.some(u => u.id === userId);
    }

    const handleFilter = (val: string) => {
        setValue(val)
    }


    const toggleUserInRole = async (userId: string) => {
        const respone = await client.post(toggleUserInRoleEndpoint, {userId: userId, roleId: selectedRole.id});
        await loadRoleUsers();
    }

    const columns = [
        {
            flex: 0.1,
            minWidth: 50,
            field: '',
            headerName: 'In Role',
            renderCell: ({ row }: CellType) => {
                return (
                    <Typography variant='body2' noWrap>
                        <Checkbox checked={isUserInRole(row.id)} onClick={()=> toggleUserInRole(row.id) }/>
                    </Typography>
                )
            }
        },
        {
            flex: 0.2,
            minWidth: 230,
            field: 'fullName',
            headerName: 'User',
            renderCell: ({ row }: CellType) => {
                const { id, fullName, email } = row

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {renderClient(row)}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                            <Link to={`/apps/user/view/${id}`}>
                                <Typography
                                    noWrap
                                    component='a'
                                    variant='body2'
                                    sx={{ fontWeight: 600, color: 'text.primary', textDecoration: 'none' }}
                                >
                                    {fullName}
                                </Typography>
                            </Link>
                            <Link to={`/apps/user/view/${id}`}>
                                <Typography noWrap component='a' variant='caption' sx={{ textDecoration: 'none' }}>
                                    {email}
                                </Typography>
                            </Link>
                        </Box>
                    </Box>
                )
            }
        },
        {
            flex: 0.2,
            minWidth: 250,
            field: 'email',
            headerName: 'Email',
            renderCell: ({ row }: CellType) => {
                return (
                    <Typography variant='body2' noWrap>
                        {row.email}
                    </Typography>
                )
            }
        },
        {
            flex: 0.15,
            field: 'roleName',
            minWidth: 150,
            headerName: 'Active Role',
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {userRoleObj[row.roleName]}
                        <Typography noWrap sx={{ color: 'text.secondary', textTransform: 'capitalize' }}>
                            {row.roleName}
                        </Typography>
                    </Box>
                )
            }
        },
        {
            flex: 0.1,
            minWidth: 110,
            field: 'status',
            headerName: 'Status',
            renderCell: ({ row }: CellType) => {
                return (
                    <CustomChip
                        skin='light'
                        size='small'
                        label={row.status}
                        color={userStatusObj[row.status]}
                        sx={{ textTransform: 'capitalize' }}
                    />
                )
            }
        },
        {
            flex: 0.1,
            minWidth: 100,
            sortable: false,
            field: 'actions',
            headerName: 'Actions',
            renderCell: ({ row }: CellType) => (
                <Link to={`/apps/user/view/${row.id}`}>
                    <IconButton>
                        <EyeOutline />
                    </IconButton>
                </Link>
            )
        }
    ]

    return (
        <Grid container spacing={6} ref={userTableRef }>
            <Grid item xs={12}>
                <Card>
                    <TableHeader value={value} handleFilter={handleFilter}  />
                    <DataGrid
                        autoHeight
                        rows={filteredUsers}
                        columns={columns}
                        pageSize={pageSize}
                        disableSelectionOnClick
                        rowsPerPageOptions={[10, 25, 50]}
                        onPageSizeChange={newPageSize => setPageSize(newPageSize)}
                    />
                </Card>
            </Grid>
        </Grid>
    )
}

export default UserList
